import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular } from 'components';
import {
  getCustomerRewardsApi,
  getCustomerRewardsBalanceApi
} from 'api';
import { useStyles } from '../../orders/styles';
import CustomerRewardRow from './CustomerRewardRow';


const CustomerRewardsList = ({customerId}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    rewardsBalance,
    rewardTransactions,
    rewardTransactionsTotalCount,
    loadingRewardTransactions
  } = useSelector((state: IReduxState) => state.customers);

  useEffect(() => {
    getCustomerRewardsApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: [
        {
          field: 'customer_id',
          value: customerId,
          conditionType: 'eq'
        }
      ],
      searchConditions: []
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    customerId
  ]);

  useEffect(() => {
    getCustomerRewardsBalanceApi({dispatch,
      sortDirection: 'asc',
      sortField: 'customer_id',
      pageSize: 1,
      currentPage: 1,
      filterConditions: [
        {
          field: 'customer_id',
          value: customerId,
          conditionType: 'eq'
        }
      ],
      searchConditions: []});
  }, [customerId]);

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <>
      <div className="flex justify-end mb-5">
        <div className="px-10 py-5 w-full max-w-300 text-center border-radius-8 bg-light-primary elevation-z8">
          {`Current Balance: ${rewardsBalance?.point_balance || 0} point${rewardsBalance?.point_balance == 1 ? '' : 's'}`}
        </div>
      </div>
      <Card elevation={20}>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="collapsible table" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell width="100px">
                    <TableSortLabel
                      active={sortField === 'reward_id'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'reward_id')}
                    >
                      <div className="px-5">ID</div>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'action_code'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'action_code')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'point_amount'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'point_amount')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'status'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'created_at'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'created_at')}
                    >
                      Create On
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'expiration_date'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'expiration_date')}
                    >
                      Expire On
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!rewardTransactions.length ?
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No matching reward transactions found.
                    </TableCell>
                  </TableRow>
                  :
                  <>
                    {rewardTransactions.map((reward) => (
                      <CustomerRewardRow
                        key={reward.transaction_id}
                        reward={reward}
                      />
                    ))}
                  </>
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!rewardTransactionsTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={rewardTransactionsTotalCount}
              rowsPerPage={rowsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
        <BackdropCircular loading={loadingRewardTransactions} />
      </Card>
    </>
  );
};

export default CustomerRewardsList;
